import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router';
import { ErrorBoundary } from 'react-error-boundary';

import { DashboardLayout } from 'src/layouts/dashboard';
import { ProductsProvider } from 'src/providers/ProductsProvider';
import { ManagersProvider } from 'src/providers/ManagersProvider';
import { StoreLocationsProvider } from 'src/providers/StoreLocationsProvider';
import { ProductModifiersProvider } from 'src/providers/ProductModifiersProvider';
import { ProductCategoriesProvider } from 'src/providers/ProductCategoriesProvider';
import { ProductMainCategoriesProvider } from 'src/providers/ProductMainCategoriesProvider';

import { LoadingScreen } from 'src/components/loading-screen';
import { ErrorPage } from 'src/components/ErrorPage/ErrorPage';

import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';

// ----------------------------------------------------------------------

const IndexPage = lazy(() => import('src/pages/dashboard/Dashboard'));
/** Products */
const ManageProducts = lazy(() => import('src/pages/products/manage/ManageProducts'));
const AddProduct = lazy(() => import('src/pages/products/add/AddProduct'));
const EditProduct = lazy(() => import('src/pages/products/add/EditProduct'));
/** Categories */
const MainCategories = lazy(() => import('src/pages/categories/main/MainCategories'));
const SubCategories = lazy(() => import('src/pages/categories/SubCategories/SubCategories'));
/** ORDERS */
const ManageOrders = lazy(() => import('src/pages/orders/ManageOrders'));
const ViewOrder = lazy(() => import('src/pages/orders/ViewOrder'));
/** Modifiers */
const AddModifier = lazy(() => import('src/pages/modifiers/add/AddModifier'));
const EditModifier = lazy(() => import('src/pages/modifiers/add/EditModifier'));
const ManageModifiers = lazy(() => import('src/pages/modifiers/manage/ManageModifiers'));
/** Balance Transfers */
const ManageBalanceTransfers = lazy(
  () => import('src/pages/balanceTransfers/ManageBalanceTransfers/ManageBalanceTransfers')
);
/** Rewards */
/** IAM */
const ManageIAM = lazy(() => import('src/pages/iam/manage/ManageIAM'));
const EditUser = lazy(() => import('src/pages/iam/manage/edit/EditUser'));
const AddUser = lazy(() => import('src/pages/iam/manage/edit/AddUser'));
/** Locations */
const ManageLocations = lazy(() => import('src/pages/locations/ManageLocations'));
const EditLocation = lazy(() => import('src/pages/locations/edit/EditLocation'));
/** Sync Accounts */
const ManageSyncAccountRequests = lazy(
  () => import('src/pages/syncAccount/ManageSyncAccountRequests')
);
/** Notifications*/
const ManageNotifications = lazy(
  () => import('src/pages/notifications/ManageNotifications/ManageNotifications')
);
/** Events */
const ManageEvents = lazy(() => import('src/pages/events/ManageEvents/ManageEvents'));
/** Customers */
const ManageCustomers = lazy(() => import('src/pages/customers/ManageCustomers'));
const CustomerPage = lazy(() => import('src/pages/customers/CustomerPage'));
/** App Settings */
const ProductSettings = lazy(() => import('src/pages/appSettings/ProductSettings'));
// ----------------------------------------------------------------------

const DashboardProviders = () => {
  const location = useLocation();

  return (
    <DashboardLayout
      slotProps={{
        main: {
          sx: {
            height: 'calc(100% - 72px)',
          },
        },
      }}
    >
      <ErrorBoundary resetKeys={[location.pathname]} fallback={<ErrorPage />}>
        <RoleBasedGuard>
          <ProductsProvider>
            <ProductModifiersProvider>
              <StoreLocationsProvider>
                <ProductMainCategoriesProvider>
                  <ProductCategoriesProvider>
                    <Suspense fallback={<LoadingScreen />}>
                      <Outlet />
                    </Suspense>
                  </ProductCategoriesProvider>
                </ProductMainCategoriesProvider>
              </StoreLocationsProvider>
            </ProductModifiersProvider>
          </ProductsProvider>
        </RoleBasedGuard>
      </ErrorBoundary>
    </DashboardLayout>
  );
};

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardProviders />
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      {
        path: 'products',
        children: [
          { element: <ManageProducts />, index: true },
          {
            element: <AddProduct />,
            path: 'new-product',
          },
          {
            element: <EditProduct />,
            path: ':id/edit',
          },
        ],
      },
      {
        path: 'modifiers',
        children: [
          {
            element: <ManageModifiers />,
            index: true,
          },
          {
            element: <AddModifier />,
            path: 'new-modifier',
          },
          {
            element: <EditModifier />,
            path: ':id/edit',
          },
        ],
      },
      {
        path: 'categories',
        children: [
          { element: <MainCategories />, index: true },
          { element: <SubCategories />, path: 'sub-categories' },
        ],
      },
      {
        path: 'orders',
        children: [
          {
            element: <ManageOrders />,
            index: true,
          },
          {
            element: <ViewOrder />,
            path: ':id',
          },
        ],
      },
      {
        path: 'balance-transfers',
        children: [
          {
            index: true,
            element: <ManageBalanceTransfers />,
          },
        ],
      },
      {
        path: 'iam',
        element: (
          <ManagersProvider>
            <Outlet />
          </ManagersProvider>
        ),
        children: [
          {
            index: true,
            element: <ManageIAM />,
          },
          {
            path: ':id/edit',
            element: <EditUser />,
          },
          {
            path: 'new-manager',
            element: <AddUser />,
          },
        ],
      },
      {
        path: 'locations',
        children: [
          {
            index: true,
            element: <ManageLocations />,
          },
          {
            element: <EditLocation />,
            path: ':id/edit',
          },
        ],
      },
      {
        path: 'sync-account',
        children: [
          {
            index: true,
            element: <ManageSyncAccountRequests />,
          },
        ],
      },
      {
        path: 'notifications',
        children: [
          {
            index: true,
            element: <ManageNotifications />,
          },
        ],
      },
      {
        path: 'events',
        children: [
          {
            index: true,
            element: <ManageEvents />,
          },
        ],
      },
      {
        path: 'customers',
        children: [
          { index: true, element: <ManageCustomers /> },
          { path: ':id/*', element: <CustomerPage /> },
        ],
      },
      {
        path: 'app-settings',
        children: [
          {
            path: 'product-settings',
            element: <ProductSettings />,
          },
        ],
      },
    ],
  },
];
